// ─── paramsToFormState ────────────────────────────────────────────────────
// Reverse-map a wire-shape `entry.params` (as produced by buildBriefRequest +
// activeMode / socialChannel / emailChannel) back into UI form-state, ready
// for dispatch SET_BATCH. Mode-specific params are reconstructed from
// content_params; research_sources are split back into kbSearch/webSearch/
// refs/upload toggles. tone_hint is inverted via toneStateFromParams.

import {
  DEFAULT_LINKEDIN_PARAMS,
  DEFAULT_CAROUSEL_PARAMS,
  DEFAULT_BLOG_PARAMS,
  DEFAULT_EMAIL_PARAMS,
  DEFAULT_ADS_PARAMS,
  DEFAULT_CASE_STUDY_PARAMS,
  DEFAULT_LANDING_PARAMS,
} from '../constants/defaultParams.js';
import { toneStateFromParams } from './toneMigration.js';
import { parseTaskContract }   from './dateEnforcement.js';

// ─── Blog-SEO keywords deserializer ────────────────────────────────────────
// Wire shape OR legacy comma-string → FE-internal {primary, secondary[]}.
// Default (empty/missing): {primary: '', secondary: 3-empty-rows} so the form
// shows the affordance even on first load.
function emptyKeywordsState() {
  return {
    primary: '',
    secondary: [
      { keyword: '', monthly_volume: null },
      { keyword: '', monthly_volume: null },
      { keyword: '', monthly_volume: null },
    ],
  };
}

export function deserializeKeywords(input) {
  // Empty/absent → fresh editable state with 3 empty rows
  if (input === undefined || input === null || input === '') {
    return emptyKeywordsState();
  }
  // Object: trust the wire shape but defensively normalize
  if (typeof input === 'object' && !Array.isArray(input)) {
    const primary = typeof input.primary === 'string' ? input.primary : '';
    const rawSec = Array.isArray(input.secondary) ? input.secondary : [];
    const secondary = rawSec.map(row => ({
      keyword: typeof row?.keyword === 'string' ? row.keyword : '',
      monthly_volume:
        row?.monthly_volume === null || row?.monthly_volume === undefined
          ? null
          : Number.isFinite(row.monthly_volume)
            ? row.monthly_volume
            : null,
    }));
    return { primary, secondary };
  }
  // Legacy string: comma-split, first token → primary, rest → secondary
  if (typeof input === 'string') {
    const tokens = input.split(',').map(s => s.trim()).filter(s => s.length > 0);
    if (tokens.length === 0) return emptyKeywordsState();
    const [primary, ...rest] = tokens;
    const secondary = rest.map(keyword => ({ keyword, monthly_volume: null }));
    return { primary, secondary };
  }
  // Anything else (number, boolean, array) → treat as empty
  return emptyKeywordsState();
}

export function paramsToFormState(params) {
  if (!params) return {};
  const cp = params.content_params   || {};
  const rs = params.research_sources || {};
  const tone = toneStateFromParams(params);
  const taskText = params.topic || '';

  const activeMode = params.activeMode || 'linkedin-post';
  const modeParams = {};

  if (activeMode === 'linkedin-post') {
    if (params.content_format === 'carousel') {
      modeParams.carouselParams = {
        ...DEFAULT_CAROUSEL_PARAMS,
        slideCount: cp.slide_count || DEFAULT_CAROUSEL_PARAMS.slideCount,
      };
    } else {
      modeParams.linkedInParams = {
        ...DEFAULT_LINKEDIN_PARAMS,
        postLength: cp.post_length || DEFAULT_LINKEDIN_PARAMS.postLength,
        ctaType:    cp.cta_type    || DEFAULT_LINKEDIN_PARAMS.ctaType,
      };
    }
  } else if (activeMode === 'blog-seo') {
    modeParams.blogParams = {
      ...DEFAULT_BLOG_PARAMS,
      articleLength: cp.article_length || DEFAULT_BLOG_PARAMS.articleLength,
      seoIntent:     cp.seo_intent     || DEFAULT_BLOG_PARAMS.seoIntent,
      keywords:      deserializeKeywords(cp.keywords),
    };
    if (cp.sitemap_url) modeParams.sitemapUrl = cp.sitemap_url;
  } else if (activeMode === 'email') {
    modeParams.emailParams = {
      ...DEFAULT_EMAIL_PARAMS,
      emailLength:          cp.email_length          || DEFAULT_EMAIL_PARAMS.emailLength,
      subjectStyle:         cp.subject_style         || DEFAULT_EMAIL_PARAMS.subjectStyle,
      articleStyle:         cp.article_style         || DEFAULT_EMAIL_PARAMS.articleStyle,
      includeCompanionPost: cp.include_companion_post ?? DEFAULT_EMAIL_PARAMS.includeCompanionPost,
    };
  } else if (activeMode === 'ads') {
    modeParams.adsParams = {
      ...DEFAULT_ADS_PARAMS,
      channel:      cp.ads_channel   || DEFAULT_ADS_PARAMS.channel,
      adsObjective: cp.ads_objective || DEFAULT_ADS_PARAMS.adsObjective,
      adGroups:     cp.ad_groups     || DEFAULT_ADS_PARAMS.adGroups,
      adCount:      cp.ad_count      || DEFAULT_ADS_PARAMS.adCount,
      groupTopics:  cp.group_topics  || DEFAULT_ADS_PARAMS.groupTopics,
    };
  } else if (activeMode === 'case-study') {
    modeParams.caseStudyParams = {
      ...DEFAULT_CASE_STUDY_PARAMS,
      outputStyle: cp.output_style || DEFAULT_CASE_STUDY_PARAMS.outputStyle,
      focusArea:   cp.focus_area   || DEFAULT_CASE_STUDY_PARAMS.focusArea,
    };
  } else if (activeMode === 'landing') {
    modeParams.landingParams = {
      ...DEFAULT_LANDING_PARAMS,
      headlineFormula: cp.headline_formula || DEFAULT_LANDING_PARAMS.headlineFormula,
      socialProof:     cp.social_proof     || DEFAULT_LANDING_PARAMS.socialProof,
    };
  }

  // referenceUrls: pad back to 3 slots (UI invariant)
  const refs = Array.isArray(rs.request_urls) ? rs.request_urls : [];
  const referenceUrls = [refs[0] || '', refs[1] || '', refs[2] || ''];

  // contentFormat applies only to linkedin-post
  const contentFormat =
    activeMode === 'linkedin-post' && params.content_format === 'carousel'
      ? 'carousel'
      : 'post';

  const kbEnabled = !!(rs.use_company_website || rs.use_company_docs);
  const webEnabled = !!rs.use_web_search;
  const uploadEnabled = !!rs.use_uploaded_documents;

  return {
    focusTopic:   params.additional_context || '',
    taskText,
    taskContract: parseTaskContract(taskText),
    keyPoints:    Array.isArray(params.key_points) ? params.key_points.join('\n') : '',
    activeMode,
    contentFormat,
    socialChannel: params.socialChannel || 'instagram',
    emailChannel:  params.emailChannel  || 'email-classic',
    persona:   params.persona   || 'cmo-marketing-leader',
    objective: params.objective || 'customer-engagement',
    ...tone,
    ctaText:       params.cta     || '',
    ctaUrl:        params.cta_url || '',
    referenceUrls,
    refsEnabled:   refs.length > 0,
    kbSearch: {
      enabled:           kbEnabled,
      query:             rs.kb_search_query || '',
      useForGeneration:  true,
      generationResults: 3,
    },
    webSearch: {
      enabled:           webEnabled,
      query:             rs.web_search_query || '',
      useForGeneration:  true,
      generationResults: rs.web_search_max_results || 3,
    },
    userKbEnabled: uploadEnabled,
    userKbQuery:   rs.upload_search_query || '',
    searchEnabled: kbEnabled || webEnabled || uploadEnabled,
    ...modeParams,
  };
}
